import { useMutation } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { getClubCode } from '@/lib/tagging';
import useAppointmentStore, {
  isInBranchAppointment as isInBranchAppointmentSelector,
} from '@/store/AppointmentStore';
import type { Confirmation } from '../utils/Confirmation';
import type { CreateAppointmentData } from '../utils/CreateAppointmentData';
import type { ProcessCustomerAccountData } from '../utils/ProcessCustomerAccountData';
import { SF_APPOINTMENT_TYPE } from '../utils/sfAppointmentTypeMap';
import { postConfirmation } from './postConfirmation';
import { postProcessCustomerAccount } from './postProcessCustomerAccount';

export const APPOINTMENT_CREATION_MUTATION_KEY = ['appointment-creation'];

export const useAppointmentCreation = () => {
  const {
    destinations,
    contactInfo,
    selectedDestination,
    searchedDestination,
    userSearch,
    selectedAgent,
    shouldUseDefaultWorktype,
    selectedDate,
    selectedStartTime,
    selectedEndTime,
    selectedAppointmentType,
    setCustomerId,
    setAccountId,
    setConfirmedAppointment,
    setAppointmentId,
    setConfirmationId,
  } = useAppointmentStore();

  const clubCode = useMemo(() => getClubCode(), []);

  const usaCanadaWorkTypeId =
    destinations?.find(
      (destination) =>
        destination.destinationApiName === 'United_States_&_Canada',
    )?.workTypeId || '';

  const isInBranchAppointment = useAppointmentStore(
    isInBranchAppointmentSelector,
  );

  const workTypeId = useMemo(() => {
    if (shouldUseDefaultWorktype && isInBranchAppointment) {
      return usaCanadaWorkTypeId;
    }

    return selectedDestination.workTypeId ?? '';
  }, [
    shouldUseDefaultWorktype,
    isInBranchAppointment,
    usaCanadaWorkTypeId,
    selectedDestination,
  ]);

  const appointmentCreationMutation = useMutation({
    mutationKey: APPOINTMENT_CREATION_MUTATION_KEY,
    mutationFn: async (
      processCustomerAccountData: ProcessCustomerAccountData,
    ): Promise<Confirmation> => {
      const customerAccount = await postProcessCustomerAccount(
        processCustomerAccountData,
      );

      setCustomerId(customerAccount.customerId);
      setAccountId(customerAccount.accountId);

      const destinationNote = searchedDestination || userSearch;

      const appointmentType =
        SF_APPOINTMENT_TYPE[selectedAppointmentType ?? 'online'];
      const communicationPreference =
        contactInfo.communicationPreference ?? 'Phone';

      const createAppointmentData: CreateAppointmentData = {
        workTypeId,
        serviceTerritoryId: selectedAgent.serviceTerritoryId ?? '',
        schedStartTime: selectedStartTime ?? '',
        schedEndTime: selectedEndTime ?? '',
        appointmentType,
        communicationPreference,
        serviceResourceId: selectedAgent.resourceId ?? '',
        agentRecordId: selectedAgent.agentRecordId ?? '',
        firstName: contactInfo.firstName ?? '',
        lastName: contactInfo.lastName ?? '',
        phone: contactInfo.phoneNumber ?? '',
        email: contactInfo.email ?? '',
        zipCode: contactInfo.zipCode ?? '',
        typeOfTripDesired: contactInfo.typeOfTrip ?? [],
        whenDoYouPlanToTravel: contactInfo.expectedTravelTime ?? '',
        destinationsRequested: selectedDestination.destinationApiName ?? '',
        clubCode,
        additionalNotes: destinationNote
          ? `ChosenDestination:'${destinationNote}', UserNotes: '${contactInfo.additionalInformation ?? ''}'`
          : `${contactInfo.additionalInformation ?? ''}`,
        accountId: customerAccount.accountId ?? '',
        macsCustomerId: customerAccount.customerId ?? '',
      };

      const confirmation = await postConfirmation(createAppointmentData);

      setConfirmedAppointment({
        agentInfo: selectedAgent,
        selectedDate,
        selectedStartTime,
        selectedEndTime,
      });
      setAppointmentId(confirmation.appointmentId);
      setConfirmationId(confirmation.confirmationId);

      return confirmation;
    },
  });

  const handleStartAppointmentCreation = useCallback(() => {
    const processCustomerAccountData: ProcessCustomerAccountData = {
      firstName: contactInfo.firstName ?? '',
      lastName: contactInfo.lastName ?? '',
      phone: contactInfo.phoneNumber ?? '',
      email: contactInfo.email ?? '',
      zipCode: contactInfo.zipCode ?? '',
      clubCode,
    };

    appointmentCreationMutation.mutate(processCustomerAccountData);
  }, [
    contactInfo.firstName,
    contactInfo.lastName,
    contactInfo.phoneNumber,
    contactInfo.email,
    contactInfo.zipCode,
    clubCode,
    appointmentCreationMutation.mutate,
  ]);

  return {
    handleStartAppointmentCreation,
  };
};
